import { useEffect, useRef, useState } from 'react';

import { configurePdfWorker, pdfjs } from 'utils/pdfWorker';

export const DEFAULT_PDF_DOCUMENT_OPTIONS = {
    cMapUrl: 'cmaps/',
    cMapPacked: true,
    isEvalSupported: false,
    wasmUrl: '/wasm/',
};

const destroyLoadingTask = (loadingTask) => {
    if (!loadingTask || typeof loadingTask.destroy !== 'function') {
        return;
    }

    const result = loadingTask.destroy();

    if (result?.catch) {
        result.catch(() => {});
    }
};

export default function usePdfDocument(fileUrl, options = DEFAULT_PDF_DOCUMENT_OPTIONS) {
    const [pdf, setPdf] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const loadingTaskRef = useRef(null);

    useEffect(() => {
        if (!fileUrl) {
            destroyLoadingTask(loadingTaskRef.current);
            loadingTaskRef.current = null;
            setPdf(null);
            setNumPages(null);
            setError(null);
            setIsLoading(false);
            return undefined;
        }

        configurePdfWorker();

        let cancelled = false;

        setIsLoading(true);
        setError(null);
        setPdf(null);
        setNumPages(null);

        const loadingTask = pdfjs.getDocument({
            ...DEFAULT_PDF_DOCUMENT_OPTIONS,
            ...options,
            url: fileUrl,
        });

        loadingTaskRef.current = loadingTask;

        loadingTask.promise
            .then((doc) => {
                if (cancelled) {
                    return;
                }

                setPdf(doc);
                setNumPages(doc.numPages);
                setIsLoading(false);
            })
            .catch((loadError) => {
                if (cancelled) {
                    return;
                }

                console.error(loadError);
                setError(loadError);
                setPdf(null);
                setNumPages(null);
                setIsLoading(false);
            });

        return () => {
            cancelled = true;
            destroyLoadingTask(loadingTask);
            loadingTaskRef.current = null;
        };
    }, [fileUrl, options]);

    return { pdf, numPages, isLoading, error };
}
